// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	RendererProps,
} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {AccordionNode} from '@app/features/messaging/utils/markdown/parser/Nodes';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {CaretRightIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback, useState} from 'react';

export function AccordionRenderer({
	node,
	id,
	renderChildren,
}: RendererProps<AccordionNode>): React.ReactElement {
	const [isOpen, setIsOpen] = useState(node.isOpenByDefault ?? false);

	const handleToggle = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setIsOpen((prev) => !prev);
	}, []);

	return (
		<div
			key={id}
			className={clsx(markupStyles.accordion, isOpen && markupStyles.accordionOpen)}
			data-flx="messaging.markdown.accordion"
		>
			<button
				type="button"
				className={markupStyles.accordionHeader}
				onClick={handleToggle}
				aria-expanded={isOpen}
				data-flx="messaging.markdown.accordion.header"
			>
				<CaretRightIcon
					size={14}
					weight="bold"
					className={clsx(markupStyles.accordionCaret, isOpen && markupStyles.accordionCaretOpen)}
				/>
				<span className={markupStyles.accordionTitle} title={node.title}>
					{node.title}
				</span>
				<span className={markupStyles.accordionBadge}>
					{isOpen ? 'Hide' : 'Show'}
				</span>
			</button>
			{isOpen && (
				<div className={markupStyles.accordionBody} data-flx="messaging.markdown.accordion.body">
					{renderChildren(node.children)}
				</div>
			)}
		</div>
	);
}
